<template>
  <loading-placeholder v-if="isLoading" :lines="10" class="home-table-desktop__loading" />
  <table v-else class="home-table-desktop">
    <thead>
      <tr>
        <th class="col--bulk-edit"> 
          <dd-checkbox
            v-model="selectAll"
            dd-size="medium"
            class="bulk-edit-checkbox"
            @change="(selected) => handleSelectAll(selected)"
          />
        </th>
        <th :colspan="getColSpanForHeader()" class="col--header">
          {{itemName}} 
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in tableData" :key="item.id" :class="getRowClass(index)">
        <!-- Checkbox for bulk edit (non-all listing only) -->
        <td class="col--bulk-edit">
          <dd-checkbox
            v-model="rowSelections[index]"
            dd-size="medium"
            class="bulk-edit-checkbox"
            @change="handleSelect"
          />
        </td>
        <!-- Status -->
        <td class="col--status">
          <router-link :to="getDetailRouterLink(item)">
            <status-chicklet
              :status="item.status"
              size="small"
              type="flat"
            />
          </router-link>
        </td>
        <!-- Customer (convos only) -->
        <td v-if="isMailbox" class="col--customer">
          <router-link :to="getDetailRouterLink(item)">
            <div :title="getCustomerNameAndEmail(item.customer)" class="customer-chicklet">
              <div>
                <avatar :size="14" :person="item.customer" show-name-on-hover />
                <span>{{ item.customer.name }}</span>
              </div>
              <div v-if="item.customer.name !== item.customer.email" class="customer-email">{{item.customer.email}}</div>
            </div>
          </router-link>
        </td>
        <!-- Title / desc -->
        <td
          :class="getItemInfoClass(item)"
          :colspan="getItemInfoColSpan(item)"
          class="col--item-info"
        >
          <!-- Lines are absolutely positioned within a fixed-height block, so each one's slot is computed from what the item has to show -->
          <div class="lines">
            <div v-if="fromAll" class="line line-1">
              <router-link :to="getLandingRouterLink(item)" class="just-a-link">
                {{ item.parent.name }}
              </router-link>
            </div>
            <router-link :to="getDetailRouterLink(item)">
              <div :class="lineClass(titleLine())" class="line">
                <span :class="{'new': item.hasUpdates }" class="item-title">#{{ item.refNumber }}: {{ item.title }}</span>
              </div>
              <div v-if="hasDesc(item)" :class="lineClass(descLine(item))" class="line">
                <span :title="item.text" class="item-desc">{{item.text}}</span>
              </div>
              <div v-if="hasTags(item)" :class="lineClass(tagsLine(item))" class="line task-tags-wrapper">
                <div class="task-tags-container">
                  <span
                    v-for="tag in item.tags"
                    :key="tag"
                    class="task-tag"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </router-link>
          </div>
          <!-- Custom fields sit in normal flow below the line grid so they can wrap and grow the row -->
          <router-link
            v-if="hasCustomFieldValues(item)"
            :to="getDetailRouterLink(item)"
            class="custom-fields-wrapper"
          >
            <custom-field-value-pills :fields="customFields" :values="item.listCustomFieldValues" />
          </router-link>
        </td>
        <!-- Meta counts -->
        <td v-if="hasCounts(item)" class="col--counts">
          <div>
            <span v-if="item.linkedTasksCount > 0" title="Linked tasks">
              <i class="ddi ddi-task" /> 
              <span>{{ item.linkedTasksCount }}</span>
            </span>
            <span v-if="item.linkedConversationsCount > 0" title="Linked conversations">
              <i class="ddi ddi-conversation" /> 
              <span>{{ item.linkedConversationsCount }}</span>
            </span>
            <span v-if="item.threadCount > 0" title="Thread count">
              <i class="ddi ddi-chat-balloon" /> 
              <span>{{ item.threadCount }}</span>
            </span>
            <span v-if="item.totalChecklistItemsCount > item.completedChecklistItemsCount" title="Checklist count">
              <i class="ddi ddi-everything" /> 
              <span>({{ item.completedChecklistItemsCount }}/{{ item.totalChecklistItemsCount }})</span>
            </span>
          </div>
        </td>
        <!-- Due Date -->
        <td v-if="item.dueDate" class="col--due-date">
          <router-link :to="getDetailRouterLink(item)">
            <due-date-chicklet :due-date="item.dueDate" />
          </router-link>
        </td>
        <!-- Priority -->
        <td class="col--priority">
          <router-link :to="getDetailRouterLink(item)">
            <priority-chicklet
              :priority="item.priority"
              size="small"
              icon-only
            />
          </router-link>
        </td>
        <!-- Assignee -->
        <td class="col--assigned-to">
          <router-link :to="getDetailRouterLink(item)">
            <!-- Job appointees display -->
            <div v-if="item.listJobAppointees">
              <div v-for="(j, index) in item.listJobAppointees" :key="index" class="job-appointee-wrap">
                <i v-if="isJobMandatedAssigneeActuallyTheAssignee(item, j)" class="ddi ddi-arrow-down" />
                <avatar 
                  :class="{'job-appointee-unassigned' : !isJobMandatedAssigneeActuallyTheAssignee(item, j)}"
                  :size="isJobMandatedAssigneeActuallyTheAssignee(item, j) ? normalAvatarSize : smallAvatarSize"
                  :person="j.appointee"
                  :name-prefix="j.job.name"
                  show-name-on-hover
                />
              </div>
            </div>
            <div v-else>
              <avatar :size="normalAvatarSize" :person="item.assignedTo" show-name-on-hover />
            </div>
          </router-link>
        </td>
        <!-- Last Updated -->
        <td class="col--last-updated">
          {{ item.lastUpdated }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { RouteLocationRaw } from 'vue-router'

/* Mixins */

/* Components */
import { HomeTableProps } from '@/components/listing/home-table/home-table.constants'
import PersonAvatar from '@/components/global/avatar.vue'
import DdCheckbox from '@/components/global/dd-checkbox.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import CustomFieldAssistant, {
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

/* Data types */

/* API */
import { IHomeTableItem } from '@/types/data-objects/items'
import { EEntities } from '@/types'
import { IJobAppointee, IPersonWithEmail } from '@/api/types/response.types'

export default defineComponent({
  name: 'HomeTableDesktop',

  components: {
    PersonAvatar,
    DdCheckbox,
  },

  props: {
    ...HomeTableProps,
  },

  emits: ['selected-rows'],

  data() {
    return {
      RouteAssistant,
      selectAll: false,
      rowSelections: [] as Array<boolean>,

      normalAvatarSize: 32,
      smallAvatarSize: 24,
    }
  },

  computed: {
    itemName(): string {
      return TextAssistant.itemSingularName(this.type)
    },
    isMailbox(): boolean {
      return this.type === EEntities.MAILBOXES
    },
    anySelected(): boolean {
      return this.rowSelections.some(r => r)
    },
  },

  watch: {
    tableData: {
      immediate: false,
      handler(val: IHomeTableItem[]) {
        this.rowSelections = Array<boolean>(val.length)
        this.clearBulkEdit()
      },
    },
  },

  methods: {
    /* If an item were moved from another entity with jobs and the mandates didn't map, we could be out of sync here (which is fine)*/
    isJobMandatedAssigneeActuallyTheAssignee(
      item: IHomeTableItem,
      j: IJobAppointee,
    ) {
      return (
        item.jobIDMandatedAsAssignee === j.job.id &&
        ((item.assignedTo === null && j.appointee === null) ||
          (item.assignedTo !== null &&
            j.appointee !== null &&
            item.assignedTo.id === j.appointee.id))
      )
    },
    getCustomerNameAndEmail(person: IPersonWithEmail) {
      if (person.name === person.email) {
        return person.email
      }
      return `${person.name} <${person.email}>`
    },
    clearBulkEdit() {
      this.selectAll = false
      this.handleSelectAll(false)
    },
    getColSpanForHeader() {
      return this.isMailbox ? 8 : 7
    },
    broadcastBulkEditSelection() {
      const selectedIDs = Array<number>()
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.rowSelections[i]) {
          selectedIDs.push(this.tableData[i].id)
        }
      }
      this.$emit('selected-rows', selectedIDs)
    },
    getRowClass(index: number): string {
      if (this.anySelected) {
        if (this.isMailbox) {
          if (this.rowSelections[index]) {
            return 'mailbox selected'
          } else {
            return 'mailbox unselected'
          }
        } else {
          if (this.rowSelections[index]) {
            return 'selected'
          } else {
            return 'unselected'
          }
        }
      }
      return ''
    },
    handleSelect() {
      this.selectAll = !this.rowSelections.some(r => !r)
      this.broadcastBulkEditSelection()
    },
    handleSelectAll(selected: any) {
      const newRowSelections = Array<boolean>(this.rowSelections.length)
      for (let i = 0; i < this.rowSelections.length; i++) {
        newRowSelections[i] = selected
      }
      this.rowSelections = newRowSelections
      this.broadcastBulkEditSelection()
    },
    getDetailRouterLink(row: IHomeTableItem): RouteLocationRaw {
      if (this.isMailbox) {
        return RouteAssistant.getConversationRoute(
          this.$route.params.accountId,
          row.parent.id,
          row.id.toString(),
          row.parent.name,
          row.refNumber,
          row.title,
          null,
          this.fromAll,
          this.fromEntityHome,
        )
      } else {
        return RouteAssistant.getTaskRoute(
          this.$route.params.accountId,
          row.parent.id,
          row.id.toString(),
          this.$permissions,
          row.parent.name,
          row.refNumber,
          row.title,
          null,
          this.fromAll,
          this.fromEntityHome,
        )
      }
    },
    getLandingRouterLink(row: IHomeTableItem): RouteLocationRaw {
      const entityID = row.parent.id

      if (this.isMailbox) {
        return RouteAssistant.getMailboxLandingRoute(
          this.$route.params.accountId,
          entityID,
        )
      } else {
        return RouteAssistant.getProjectLandingRoute(
          this.$route.params.accountId,
          entityID,
          this.$permissions,
        )
      }
    },
    hasCounts(item: IHomeTableItem): boolean {
      return (
        this.hasLinkedTasks(item) ||
        this.hasLinkedConversations(item) ||
        this.hasThreadCount(item) ||
        this.hasChecklistCount(item)
      )
    },
    hasLinkedTasks(item: IHomeTableItem): boolean {
      return item.linkedTasksCount > 0
    },
    hasLinkedConversations(item: IHomeTableItem): boolean {
      return item.linkedConversationsCount && item.linkedConversationsCount > 0
    },
    hasThreadCount(item: IHomeTableItem): boolean {
      return item.threadCount > 0
    },
    hasChecklistCount(item: IHomeTableItem): boolean {
      return item.totalChecklistItemsCount > item.completedChecklistItemsCount
    },
    hasDesc(item: IHomeTableItem): boolean {
      return item.text && item.text.length > 0
    },
    hasTags(item: IHomeTableItem): boolean {
      return item.tags && item.tags.length > 0
    },
    /* True when the item has a value for at least one of the listing-visible custom fields */
    hasCustomFieldValues(item: IHomeTableItem): boolean {
      if (!this.customFields.length || !item.listCustomFieldValues) {
        return false
      }
      return this.customFields.some(field => {
        const value = item.listCustomFieldValues.find(
          v => v.customFieldID === field.id,
        )
        if (!CustomFieldAssistant.hasValue(field, value || null)) {
          return false
        }
        // An unchecked checkbox renders nothing, so it shouldn't claim a line
        return (
          field.customFieldType !== ECustomFieldTypes.Checkbox ||
          !!value.checkboxData
        )
      })
    },
    /* Line slot helpers: the entity name (all-items views) always takes line 1 */
    lineClass(line: number): string {
      return `line-${line}`
    },
    titleLine(): number {
      return this.fromAll ? 2 : 1
    },
    descLine(item: IHomeTableItem): number {
      return this.titleLine() + 1
    },
    tagsLine(item: IHomeTableItem): number {
      return this.titleLine() + 1 + (this.hasDesc(item) ? 1 : 0)
    },
    getItemInfoClass(item: IHomeTableItem): string {
      let lineCount = 1
      lineCount += this.fromAll ? 1 : 0

      const hasDesc = this.hasDesc(item)
      lineCount += hasDesc ? 1 : 0

      const hasTags = this.hasTags(item)
      lineCount += hasTags ? 1 : 0

      // Custom fields render below the line grid in normal flow, so they don't take a line slot
      const extraClass = this.hasCustomFieldValues(item)
        ? ' has-custom-fields'
        : ''

      switch (lineCount) {
        case 4:
          return 'four-lines' + extraClass
        case 3:
          return 'three-lines' + extraClass
        case 2:
          return 'two-lines' + extraClass
        case 1:
        default:
          return 'one-line' + extraClass
      }
    },
    getItemInfoColSpan(item: IHomeTableItem) {
      let result = 1
      if (!item.dueDate) {
        result++
      }
      if (!this.hasCounts(item)) {
        result++
      }
      return result
    },
  },
})
</script>

<style lang="scss">
.home-table-desktop {
  width: 100%;
  word-wrap: break-word;
  table-layout: auto;
  border: $border--dotted-light;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;

  .task-tags-wrapper {
    margin: 4px 0 8px 0;
    display: block !important;
    width: 100%;
    text-align: left !important;

    /* Inside the absolutely positioned line grid the slot already provides the spacing */
    &.line {
      margin: 0;

      .task-tag {
        margin-bottom: 0;
      }
    }
  }

  .task-tags-container {
    display: block;
    text-align: left;
  }

  .task-tag {
    display: inline-block;
    padding: 4px 6px;
    margin-right: 4px;
    margin-bottom: 2px;
    font-size: 12px;
    white-space: nowrap;
    line-height: 1.2;
    vertical-align: top;
    background: var(--color-background-secondary);
    color: var(--color-text-secondary);
    border-radius: 20px;
    border-color: var(--color-text-third);
    border-width: 1px;
    border-style: solid;
  }

  .task-tag:last-child {
    margin-right: 0;
  }

  &__loading {
    padding-top: 20px;
    .vue-content-placeholders-text__line {
      height: 24px;
    }
  }

  th {
    background-color: var(--color-background-secondary);
    @include lettering--h5;

    &:first-child {
      border-top-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
    }

    &.col--bulk-edit {
      padding: 0 0 0 10px;
      border-right: $border--dotted-light;

      .bulk-edit-checkbox {
        top: 2px;
      }
    }

    &.col--header {
      padding: 12px 10px;
    }
  }

  tr {
    &:hover {
      td {
        background-color: var(--color-background-primary);
        .bulk-edit-checkbox {
          visibility: visible !important;
          opacity: 1 !important;
        }
      }
    }

    /* Hide checkboxes on data rows in NON-bulk edit state... */
    &:not(.selected):not(.unselected) {
      td {
        .bulk-edit-checkbox {
          visibility: hidden;
          opacity: 0;
        }
      }
    }

    &.selected {
      > td {
        background-color: var(--color-background-blue);
      }
    }

    &.mailbox.selected {
      > td {
        background-color: var(--color-background-green);
      }
    }

    &.unselected {
      opacity: 0.7;
    }

    td {
      background-color: var(--color-background-header);
      border-bottom: $border--solid-extra-light;
      padding-top: 10px;
      padding-bottom: 10px;

      a:not(.just-a-link) {
        text-decoration: none;
        color: inherit;
      }

      &.col--bulk-edit {
        padding: 0 10px;
        border-right: $border--solid-extra-light;

        .bulk-edit-checkbox {
          top: 3px;
        }
      }

      &.col--status {
        min-width: 165px;
        max-width: 165px;
        padding: 0 10px;

        &.from-all {
          padding-right: 20px;
        }
      }

      &.col--customer {
        min-width: 175px;
        max-width: 175px;
        padding: 0 6px;

        .customer-chicklet {
          padding: 2px 4px;
          border-radius: 8px;
          border: solid 1px transparent;

          div {
            @include lettering--p-sm;
            @include single-line-ellipsis;
            padding: 0;

            .avatar {
              margin-right: 4px;
              top: -1px;
            }

            &.customer-email {
              @include lettering--p-sm--no-color;
              color: var(--color-text-third);
            }
          }
        }
      }

      &.col--item-info {
        width: 100%;
        position: relative;

        /* The fixed-height block that hosts the absolutely positioned lines */
        .lines {
          position: relative;
        }

        &.one-line .lines {
          height: 54px;

          .line-1 {
            top: 15px;
          }
        }

        &.two-lines .lines {
          height: 70px;

          .line-1 {
            top: 12px;
          }

          .line-2 {
            top: 36px;
          }
        }

        &.three-lines .lines {
          height: 91px;

          .line-1 {
            top: 12px;
          }

          .line-2 {
            top: 36px;
          }

          .line-3 {
            top: 57px;
          }
        }

        &.four-lines .lines {
          height: 115px;
          .line-1 {
            top: 11px;
          }

          .line-2 {
            top: 34px;
          }

          .line-3 {
            top: 57px;
          }

          .line-4 {
            top: 80px;
          }
        }

        /* The line grid leaves breathing room at its bottom; pull the pills up into it so spacing stays even */
        &.has-custom-fields .lines {
          margin-bottom: -10px;
        }

        .custom-fields-wrapper {
          display: block;
          padding: 0 10px 6px;

          .custom-field-value-pills__pill {
            margin-bottom: 4px;
          }
        }

        .line {
          position: absolute;
          left: 0;
          right: 0;
          padding: 0 10px;
          @include single-line-ellipsis;
          @include lettering--p-sm--no-color;
          color: var(--color-text-third);
        }

        .item-title {
          @include lettering--p;

          &.new {
            font-weight: 700;
          }
        }

        .item-desc {
          @include lettering--p-sm--no-color;
          color: var(--color-text-third);
        }

        .entity-name {
          @include lettering--p-sm;
          padding-bottom: 30px;
        }
      }

      &.col--counts {
        max-width: 125px;
        min-width: 125px;
        text-align: right;

        span {
          padding-right: 2px;
        }

        > div {
          @include single-line-ellipsis;
          color: var(--color-text-third);

          > * {
            font-size: 13px;

            > i {
              margin-right: 2px;
            }
          }
          @include flex-row-horizontal-spacing(3px);
        }
      }

      &.col--due-date {
        padding: 0 5px;
      }

      &.col--priority {
        max-width: 41px;
        min-width: 41px;
        padding: 0 10px 0 5px;

        .chicklet--small {
          padding: 2px 0 0 0;
        }
      }

      &.col--assigned-to {
        border-left: $border--solid-extra-light;
        padding: 0 10px;

        a > div {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          @include flex-row-horizontal-spacing(8px);

          > div.job-appointee-wrap {
            position: relative;

            .job-appointee-unassigned {
              opacity: 0.8;
            }

            .ddi {
              font-size: 10px;
              top: -12px;
              left: 50%;
              transform: translateX(-50%);
              position: absolute;
            }
          }
        }
      }

      &.col--last-updated {
        @include lettering--p-sm--no-color;
        white-space: nowrap;
        color: var(--color-text-third);
        text-align: right;
        padding: 0 10px;
        border-left: $border--solid-extra-light;
      }
    }

    &:last-child > td {
      &:first-child {
        border-bottom-left-radius: 8px;
      }
      &:last-child {
        border-bottom-right-radius: 8px;
      }
    }
  }
}
</style>
